/**
 * Lightweight per-prompt dismissal memory backed by localStorage.
 *
 * Each prompt key (install, ios-walkthrough, ios-chrome) gets its own
 * cooldown so a "Not now" on one banner doesn't suppress unrelated ones.
 */

import { useCallback, useEffect, useState } from 'react'

const STORAGE_PREFIX = 'taqat:pwa-prompt:'

export type PromptKey = 'install' | 'ios-walkthrough' | 'ios-chrome'

interface PromptRecord {
  dismissedAt: number
  count: number
}

export interface PromptMemory {
  /** True when the cooldown has elapsed (or the prompt was never dismissed). */
  isEligible: boolean
  /** Records a dismissal that suppresses the prompt for `cooldownDays`. */
  dismiss: () => void
  /** Permanently marks the prompt as resolved (e.g. user installed). */
  resolve: () => void
}

/**
 * Returns whether a given prompt is allowed to show right now and a `dismiss`
 * helper that writes today's timestamp into localStorage.
 */
export function usePromptMemory(key: PromptKey, cooldownDays: number): PromptMemory {
  const storageKey = STORAGE_PREFIX + key
  const [record, setRecord] = useState<PromptRecord | null>(() => readRecord(storageKey))

  useEffect(() => {
    setRecord(readRecord(storageKey))
  }, [storageKey])

  const dismiss = useCallback(() => {
    const next: PromptRecord = {
      dismissedAt: Date.now(),
      count: (record?.count ?? 0) + 1,
    }
    writeRecord(storageKey, next)
    setRecord(next)
  }, [record, storageKey])

  const resolve = useCallback(() => {
    writeRecord(storageKey, { dismissedAt: Number.MAX_SAFE_INTEGER, count: -1 })
    setRecord({ dismissedAt: Number.MAX_SAFE_INTEGER, count: -1 })
  }, [storageKey])

  const isEligible = computeEligible(record, cooldownDays)

  return { isEligible, dismiss, resolve }
}

function computeEligible(record: PromptRecord | null, cooldownDays: number): boolean {
  if (!record) return true
  if (record.count < 0) return false
  const cooldownMs = cooldownDays * 24 * 60 * 60 * 1000
  return Date.now() - record.dismissedAt > cooldownMs
}

function readRecord(key: string): PromptRecord | null {
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as PromptRecord) : null
  } catch {
    return null
  }
}

function writeRecord(key: string, value: PromptRecord): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Storage may be unavailable in private mode; we silently degrade.
  }
}
